import { FunctionComponent, useEffect, useState, useRef } from "react";
import { addNoteFirebase, changeLeadStage, fetchConstants, } from "../../../Services/contacts";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../../Redux/actions";
import { useHistory } from "react-router-dom";
import TextInput from "../../TextInput/InputText";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import DropDown from "../../../Screens/LeadDistributorLogic/Dropdown"
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import { styled } from "@mui/material/styles";
import { TextField } from "@mui/material";


const primaryColor = "#292744";

/**
 * Custom styled Material-UI TextArea for "other reason" input.
 * Rounded, theme-friendly input with hover and focus states.
 */
const CustomTextArea = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#f7f8fa",
    fontSize: "var(--font-size-large)",
    fontFamily: "var(--font-family-main)",
    "& fieldset": {
      borderColor: "#bfc7d5",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
      border: `1.5px solid ${primaryColor}`
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    fontSize: 16,
    color: "#222",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    fontSize: 15,
    color: primaryColor,
  },
});

type props = {
  /** Complete contact details including contactId */
  contactDetail: any;

  /** Trigger closing of the modal */
  close: () => void;

  /** Toggle modal visibility */
  open: boolean;

  /** List of existing tasks for this contact */
  tasksData: any[];

  /** Existing notes array used during updates */
  notes: any[];

  /** Authenticated user object */
  user: any;

  /** Theme mode - light/dark */
  theme: any;

  /** Column headers from Redux (dynamic label mapping) */
  conatctHeader: any;
};

/**
 * =============================================================
 * NotInterestedLeadModal
 * =============================================================
 * A modal component that marks a lead as **NOT INTERESTED**.
 *
 * Features:
 * - Fetches constant lists (Not Interested Reason, Stage, Type, etc.)
 * - Allows selecting a "Not Interested Reason"
 * - If reason = "Other", a text field appears
 * - Allows adding a note (optional)
 * - Adds note to Firebase if provided
 * - Updates lead stage to "NOT INTERESTED"
 * - Manages loading spinner during API operations
 * - Responsive layout (desktop + mobile friendly)
 */
const NotInterestedLeadModal: FunctionComponent<props> = ({
  contactDetail,
  close,
  open,
  tasksData,
  notes,
  user,
  theme,
  conatctHeader,
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const dispatcher = useDispatch();
  const history = useHistory();

  /** Ref for "Other Reason" input */
  const otherRef: any = useRef();

  /** Ref for the optional Note input */
  const noteRef: any = useRef();

  /** Controls spinner loader during submit */
  const [load, setLoad] = useState(false);

  /** Currently selected "Not Interested Reason" */
  const [notIntSelected, setNotIntSelected] = useState<any>({ label: contactDetail.not_int_reason ? contactDetail.not_int_reason : "Select", value: contactDetail.not_int_reason ? contactDetail.not_int_reason : "Select" });

  /** Lists fetched from constants API */
  const [propertyTypeList, setPropertyTypeList] = useState<any[]>([]);
  const [propertyStageList, setPropertyStageList] = useState<any[]>([]);
  const [nextFollowUpList, setnextFollowUpList] = useState<any[]>([]);
  const [notIntReasonList, setNotIntReasonList] = useState<any[]>([]);
  const [lostReasonList, setLostReasonList] = useState<any[]>([]);

  /** Stores custom "Other" text when user selects reason = Other */
  const [other, setOther] = useState("");

  /**
   * Fetch Lists on Mount
   * ------------------------------------------------------------
   * Fetches:
   * - Not interested reasons
   * - Lost reasons
   * - Property stage list
   * - Property type list
   * - Next follow up types
   *
   * Cleanup unsubscribes from snapshot listeners.
   */

  useEffect(() => {
    const unsubConst = fetchConstants(
      (data) => setNotIntReasonList(data),
      (data) => setLostReasonList(data),
      (data) => setPropertyStageList(data),
      (data) => setPropertyTypeList(data),
      (data) => setnextFollowUpList(data)
    );
    return () => {
      unsubConst();
    };
  }, []);

    /**
   * SUBMIT HANDLER
   * ----------------------------------------------------------------------------
   * Validates:
   * - Not Interested Reason must be selected
   * - If "Other", custom text must be provided
   *
   * Executes:
   * - Saves note (if provided)
   * - Updates contact stage → "NOT INTERESTED"
   */
  const onSubmit = () => {
     // Validation
    if (notIntSelected.value === "Select") {
      dispatcher(showSnackbarAction("Please Select Not Interested Reason", "error"));
    } else if (notIntSelected.value === "Other" && other === "") {
      dispatcher(showSnackbarAction("Please Mention Other Not Interested Reason", "error"));
    } else {
      // Save Note (optional)
      if (noteRef.current.value.length !== 0) {
        addNoteFirebase(
          contactDetail.contactId,
          notes,
          noteRef.current.value,
          () => { },
          dispatcher,
          () => { },
          user.user_email
        );
      }
       // Update Lead Stage
      changeLeadStage(
        contactDetail.contactId,
        {
          not_int_reason: notIntSelected.value,
          stage: "NOT INTERESTED",
          other_not_int_reason: other === "" ? "" : other,
        },
        (data) => setLoad(data),
        dispatcher,
        close,
        history,
        tasksData
      );
    }
  };

    /**
   * Modal dynamic styling customized per theme + mobile UI
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      width: 400,
      maxWidth: 400,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 380,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <form style={{ width: "100%" }}>
             {/* Modal Title */}
            <Box sx={{ mb: 2 }}>
              <h3
                style={{
                  color: theme ? "#272944" : "#fff",
                  fontFamily: "var(--font-family-main)",
                  fontSize: "var(--font-size-large)",
                  marginBottom: 24,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: 0.5,
                }}
              >
                Not Interested Details
              </h3>
            </Box>
             {/* MAIN BODY INPUTS */}
            <Grid container spacing={2} sx={{ width: "100%" }}>
               {/* REASON DROPDOWN */}
              <Grid item xs={12} sm={12}>
                <DropDown
                  title={conatctHeader.find((obj: any) => obj.label === "not_int_reason").value}
                  option={notIntReasonList}
                  selectedValue={notIntSelected.value === "Select" ? "" : notIntSelected}
                  setSelectedValue={(value) => {
                    setNotIntSelected(value);
                    if (value !== "Other") {
                      setOther("");
                    }
                  }}
                  required={true}
                />
              </Grid>
              {/* OTHER REASON FIELD */}
              {notIntSelected.value === "Other" && (
                <Grid item xs={12} sm={12}>
                  <CustomTextArea
                    fullWidth
                    label="Enter Other Reason"
                    variant="outlined"
                    multiline
                    rows={1}
                    ref={otherRef}
                    onChange={(e) => setOther(e.target.value)}
                    value={other}
                    InputLabelProps={{ style: { color: "#555", fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                    InputProps={{ style: { color: '#222', fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)" } }}
                  />
                </Grid>
              )}
               {/* NOTE FIELD */}
              <Grid item xs={12} sm={12}>
                <TextInput
                  title="Enter Note"
                  style={{ backgroundColor: "#fff" }}
                  ref={noteRef}
                />
              </Grid>
            </Grid>
             {/* FOOTER BUTTONS */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                  width: "100%",
                }}
              >
                <Box style={{ marginRight: 8 }}>
                  <StyledCancelButton onClick={close} isMobile={isMobile}>
                    Cancel
                  </StyledCancelButton>
                </Box>
                <Box>
                  <StyledSubmitButton
                    onClick={onSubmit}
                    themeMode={themeMUI.palette.mode}
                    isMobile={isMobile}
                  >
                    {load ? (
                      <span style={{ display: "flex", justifyContent: "center" }}>
                        <Circles color="#fff" height={20} width={20} />
                      </span>
                    ) : (
                      <>Submit</>
                    )}
                  </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </form>
        </Box>
      </Modal >
    </>
  );
};

/** Map Redux state to component props */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode,
    conatctHeader: state.contactHeaderList.data,
  };
};

export default connect(mapStateToProps)(
  NotInterestedLeadModal
);
